/**
 * BL-1056 — the SINGLE source of the lifecycle "stamp" columns: the
 * decision-timestamp columns that `deriveLotPhase` / `deriveAuctionPhase`
 * (ADR-023 predicate-as-truth) read to compute a row's phase. Writing any of
 * them is a lifecycle TRANSITION and must go through `LotStatusTransitionService`
 * / `AuctionStatusTransitionService` (ADR-033) — never a generic field update.
 *
 * Three enforcement layers all read from THIS module so the guarded set can
 * never drift from the derived set (AC 3):
 *   1. the import-fenced stamp repos (the only modules that assign these columns),
 *   2. the `Omit<…, LotLifecycleStampColumn>` patch types on the generic writers,
 *   3. the `assertNoLifecycleStamps` runtime backstop (below).
 *
 * Pure + client-safe (no `@auctogon/db` / React import) so it transits the
 * lifecycle barrel next to `phase-tokens.ts`. The column NAMES are the camelCase
 * Drizzle fields (for the `Omit` types) paired with their snake_case DB columns
 * (so the runtime assert is case-robust for snake-keyed patches — the batch-PUT
 * raw SQL and `updateLotCatalogByIdTx` speak snake_case).
 *
 * EXCLUDED by design (AC 5): `deleted_at` is dual-owned with `SoftDeleteService`
 * (ADR-018), and `close_time` / `original_close_time` are SCHEDULING, not phase
 * stamps. Neither is a `deriveLotPhase` / `deriveAuctionPhase` decision input.
 *
 * `passed_at` WAS in this set and is gone as of BL-1387 PR 2. The note that used
 * to sit here recorded that it "is never written to a non-null value anywhere
 * today" and stayed "for completeness" — that observation is what eventually
 * retired it. The column, the `passed` phase rung, and the stamp are all dropped;
 * the `passed` DISPOSITION (`auction_lots.disposition`) is untouched and remains
 * the product word for a reserve-not-met lot.
 */

/** Lot stamp columns as `{ camelCaseDrizzleField: snake_case_db_column }` — the source of truth. */
const LOT_STAMP_COLUMN_MAP = {
  readiedAt: 'readied_at',
  biddingOpenedAt: 'bidding_opened_at',
  preBiddingAt: 'pre_bidding_at',
  withdrawnAt: 'withdrawn_at',
  hammeredAt: 'hammered_at',
  closeProcessedAt: 'close_processed_at',
  pausedAt: 'paused_at',
} as const;

/** Auction stamp columns as `{ camelCaseDrizzleField: snake_case_db_column }`. */
const AUCTION_STAMP_COLUMN_MAP = {
  readiedAt: 'readied_at',
  openedAt: 'opened_at',
  closedAt: 'closed_at',
  pausedAt: 'paused_at',
} as const;

/** A camelCase lot phase-stamp column key — the `Omit` target for lot writers (AC 2). */
export type LotLifecycleStampColumn = keyof typeof LOT_STAMP_COLUMN_MAP;
/** A camelCase auction phase-stamp column key — the `Omit` target for auction writers. */
export type AuctionLifecycleStampColumn = keyof typeof AUCTION_STAMP_COLUMN_MAP;
/** Any lifecycle stamp column (lot ∪ auction), camelCase. */
export type LifecycleStampColumn = LotLifecycleStampColumn | AuctionLifecycleStampColumn;

/**
 * The snake_case DB column names for the lot phase stamps — the values of the
 * single source map. The generic bulk writers speak snake_case (their patch keys
 * are `auction_lots` DB columns, translated snake→camel by `buildLotUpdateSet`),
 * so their compile-time `Omit` (AC 2) excludes these snake keys, not the camel
 * `LotLifecycleStampColumn`. Derived from the SAME map as everything else (AC 3).
 */
export type LotLifecycleStampDbColumn = (typeof LOT_STAMP_COLUMN_MAP)[LotLifecycleStampColumn];

/**
 * Layer 2 (AC 2): the patch type for the generic snake_case lot writers
 * (`bulkUpdateLotsByIds` / `bulkUpdateLotsByAuction`). A `Record<string, unknown>`
 * that STRUCTURALLY forbids every lifecycle-stamp DB column — each stamp key is
 * typed `never`, so passing one (`{ readied_at: … }`, `{ withdrawn_at: … }`) is a
 * COMPILE error, while any non-stamp catalog column (`ring_id`, `category_id`,
 * `sort_order`, …) flows through as `unknown`. The excluded keys come from the
 * single stamp-column source, so this compile-time guard cannot drift from the
 * `assertNoLifecycleStamps` runtime backstop (AC 3). An `as`-cast that erases this
 * type is the exact bypass Layer 3's runtime assert catches.
 */
export type NonLifecycleStampLotPatch = Record<string, unknown> & {
  readonly [K in LotLifecycleStampDbColumn]?: never;
};

/** The camelCase lot stamp columns (drives `Omit<Partial<AuctionLotInsert>, …>`). */
export const LOT_LIFECYCLE_STAMP_COLUMNS = Object.keys(
  LOT_STAMP_COLUMN_MAP,
) as ReadonlyArray<LotLifecycleStampColumn>;

/** The camelCase auction stamp columns. */
export const AUCTION_LIFECYCLE_STAMP_COLUMNS = Object.keys(
  AUCTION_STAMP_COLUMN_MAP,
) as ReadonlyArray<AuctionLifecycleStampColumn>;

/**
 * Every lifecycle-stamp key the runtime assert rejects — BOTH the camelCase
 * Drizzle fields AND their snake_case DB columns, so a patch keyed in either
 * casing is caught. This is the single set layers 1–3 read from (AC 3).
 */
export const LIFECYCLE_STAMP_COLUMNS: ReadonlySet<string> = new Set<string>([
  ...Object.keys(LOT_STAMP_COLUMN_MAP),
  ...Object.values(LOT_STAMP_COLUMN_MAP),
  ...Object.keys(AUCTION_STAMP_COLUMN_MAP),
  ...Object.values(AUCTION_STAMP_COLUMN_MAP),
]);

/**
 * Runtime backstop (AC 4): throw if a patch carries ANY lifecycle-stamp column
 * (camel or snake). Wired into the generic lot writers + the batch-PUT raw-SQL
 * column gate — the ONLY sanctioned stamp-write path is the import-fenced stamp
 * repos, which never call this. Catches an `as`-cast that slips past the `Omit`
 * type (layer 2). Greppable message names the fix.
 *
 * @param patch A column patch (object whose keys are candidate column names).
 * @throws Error naming the offending stamp column when one is present.
 */
export function assertNoLifecycleStamps(patch: { readonly [key: string]: unknown }): void {
  for (const key of Object.keys(patch)) {
    if (LIFECYCLE_STAMP_COLUMNS.has(key)) {
      throw new Error(
        `Lifecycle-stamp column "${key}" written through a generic writer (BL-1056). ` +
          'Route this write through LotStatusTransitionService / AuctionStatusTransitionService ' +
          '(the import-fenced lifecycle-stamp repos are the only sanctioned stamp writers).',
      );
    }
  }
}
